import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X as XIcon, Loader2, ArrowRight, ArrowLeft, Mail, Calendar, Folder, ShieldCheck, Palette, Plug } from "lucide-react";
import { useAuth, refreshAuth, getCsrfToken } from "../lib/auth";
import { AVAILABLE_THEMES, useVisualTheme, type VisualTheme } from "../lib/themeLoader";
import { ConnectorCatalog } from "../components/ConnectorCatalog";
import { api } from "../lib/api";

/** Onboarding wizard. Six skippable steps (handle is the only
 *  required one). Tracks step both in local state AND in
 *  `user_settings.onboarding_step` server-side so the wizard
 *  survives OAuth round-trips (Connect Gmail / Connect Calendar
 *  redirect through Google and back; without persistence the wizard
 *  would re-mount at step 1 and lock the user out — MEMSO-#W24).
 *
 *  Returning users (users.onboarded_at is set) see the same wizard
 *  but in "edit" framing: existing handle is shown read-only with a
 *  Change affordance, vault path is pre-populated from saved
 *  user_settings, connector steps show current connections. Returning
 *  users never see a blank pick-your-handle input — that pattern
 *  reads as account loss when their state is in fact intact. */
type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;
const STEPS: { idx: Step; label: string; icon: typeof Palette }[] = [
  { idx: 1, label: "Handle", icon: Check },
  { idx: 2, label: "Theme", icon: Palette },
  { idx: 3, label: "Vault", icon: Folder },
  { idx: 4, label: "Privacy", icon: ShieldCheck },
  { idx: 5, label: "Connectors", icon: Plug },
  { idx: 6, label: "Gmail", icon: Mail },
  { idx: 7, label: "Calendar", icon: Calendar },
];

const STEP_BY_NAME: Record<string, Step> = {
  handle: 1,
  theme: 2,
  vault: 3,
  privacy: 4,
  connectors: 5,
  gmail: 6,
  calendar: 7,
};
const NAME_BY_STEP: Record<Step, string> = {
  1: "handle",
  2: "theme",
  3: "vault",
  4: "privacy",
  5: "connectors",
  6: "gmail",
  7: "calendar",
};

/** Parse ?connected=<provider> from the current URL. Set by the
 *  Google OAuth callback (`google-oauth.ts:170`) when an OAuth
 *  round-trip lands the user back on /onboarding. We use this to
 *  jump the wizard straight to step 5 (after Gmail) or step 6
 *  (after Calendar) regardless of what the persisted step said —
 *  the URL signal is more recent than the server one. */
function readConnectedParam(): "gmail" | "calendar" | null {
  if (typeof window === "undefined") return null;
  const p = new URLSearchParams(window.location.search).get("connected");
  if (p === "gmail" || p === "calendar") return p;
  return null;
}

/** Single logout path shared by the top-right escape bar, the footer
 *  Log out link, and the "session expired" recovery in the Claim
 *  handler. /auth/logout is the real route (/api/auth/logout 401s at
 *  the /api requireAuth gate, which was the old redirect-loop bug).
 *  It clears the portable cookie on the parent brand domain. We then
 *  clear local/session storage and hard-redirect so the user can
 *  never get stuck on the onboarding screen. */
async function doLogout(reason = "signed_out"): Promise<void> {
  try {
    await fetch("/auth/logout", { method: "POST", credentials: "include" });
  } catch {
    /* still clear + redirect below */
  }
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    /* storage may be unavailable */
  }
  window.location.replace(`/login?reason=${encodeURIComponent(reason)}`);
}

async function persistStep(step: Step): Promise<void> {
  try {
    const csrf = getCsrfToken();
    await fetch("/api/user/settings", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify({ key: "onboarding_step", value: NAME_BY_STEP[step] }),
    });
  } catch {
    // Best-effort: server-side step persistence is the OAuth-survival
    // backstop. If it fails, the wizard still works for the
    // single-page-load happy path; only the OAuth round-trip case
    // would regress to step 1, which the URL param + Skip button
    // mitigate independently.
  }
}

async function readPersistedStep(): Promise<Step | null> {
  try {
    const res = await fetch("/api/user/settings/onboarding_step", {
      credentials: "same-origin",
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { value?: string };
    if (typeof body.value === "string" && STEP_BY_NAME[body.value]) {
      return STEP_BY_NAME[body.value];
    }
  } catch {
    /* fall through to default */
  }
  return null;
}

export function Onboarding() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [step, setStepState] = useState<Step>(1);
  const [stepLoaded, setStepLoaded] = useState(false);

  // Wrapper that updates local state AND persists server-side. Every
  // step transition routes through here so the OAuth round-trip
  // survival path always has the latest step in user_settings.
  const setStep = (next: Step) => {
    setStepState(next);
    void persistStep(next);
  };

  useEffect(() => {
    if (!auth.loading && !auth.authenticated) {
      navigate("/", { replace: true });
    }
  }, [auth.loading, auth.authenticated, navigate]);

  // On first mount, restore the wizard step. Priority:
  //   1. ?connected=<provider> URL param (just landed from OAuth).
  //   2. user_settings.onboarding_step (persisted across reloads).
  //   3. Default to 1 if no handle, else stay at 1 (the handle step
  //      lets the user re-confirm their existing handle and skip).
  // After computing, scrub the ?connected= param from the URL so a
  // subsequent reload doesn't keep jumping forward.
  useEffect(() => {
    if (auth.loading || !auth.authenticated) return;
    let cancelled = false;
    (async () => {
      const connected = readConnectedParam();
      let next: Step | null = null;
      if (connected === "gmail") next = 7; // landed from Gmail OAuth → calendar step
      else if (connected === "calendar") next = 7; // landed from Calendar OAuth → final step
      if (!next) next = await readPersistedStep();
      if (!next) next = 1;
      if (cancelled) return;
      setStepState(next);
      setStepLoaded(true);
      // Scrub the ?connected= flag so a refresh doesn't keep firing.
      if (connected && typeof window !== "undefined") {
        const url = new URL(window.location.href);
        url.searchParams.delete("connected");
        window.history.replaceState(null, "", url.toString());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.loading, auth.authenticated]);

  // If the user already has a handle and lands on /onboarding directly
  // (e.g. via a saved bookmark), let them through to revisit any step
  // they want; the "Done" button on each step jumps to /u/<handle>.
  const handle = auth.user?.handle || null;
  // Returning user = has completed first-run onboarding at least once.
  // Distinct from "has a handle" (which can be true mid-first-run for
  // users whose row was auto-populated from email). We branch the
  // wizard's framing + per-step rendering on this so a returning user
  // never sees a blank "pick your handle" or empty vault input — that
  // pattern looks like the account got wiped when in fact every saved
  // value is intact server-side.
  const isReturning = !!auth.user?.onboardedAt;

  const goWorkspace = async () => {
    if (!handle) return;
    // Mark onboarding complete server-side so future sign-ins land
    // straight in the workspace. Best-effort: even if the call fails
    // (network, CSRF), we still navigate to the workspace -- the
    // worst case is the user re-runs the wizard next sign-in, not a
    // broken redirect loop.
    try {
      const csrf = getCsrfToken();
      await fetch("/api/onboarding/complete", {
        method: "POST",
        credentials: "same-origin",
        headers: csrf ? { "X-CSRF-Token": csrf } : undefined,
      });
      await refreshAuth();
    } catch {
      /* fall through; navigation is the priority */
    }
    navigate(`/u/${encodeURIComponent(handle)}`, { replace: true });
  };

  const next = () => {
    if (step < 7) setStep((step + 1) as Step);
    else goWorkspace();
  };
  const back = () => {
    if (step > 1) setStep((step - 1) as Step);
  };

  if (auth.loading || !stepLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-300">
        Checking session…
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#0c2540]">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-50"
        style={{ backgroundImage: "url('/brand/memso-banner.png')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0c2540]/40 via-[#0c2540]/65 to-[#0c2540]/95" />
      <div className="absolute top-3 right-4 z-10 flex items-center gap-3 text-xs text-white/70">
        {auth.user?.email && (
          <span className="hidden sm:inline">
            Signed in as <span className="text-white/90">{auth.user.email}</span>
          </span>
        )}
        <button
          type="button"
          onClick={() => navigate("/today")}
          className="underline hover:text-white"
        >
          Cancel onboarding
        </button>
        <button
          type="button"
          onClick={() => void doLogout()}
          className="underline hover:text-white"
        >
          Wrong account? Sign out
        </button>
      </div>
      <div
        className="relative w-full max-w-lg rounded-3xl border border-white/15 backdrop-blur-xl p-6 sm:p-8 space-y-5 shadow-2xl"
        style={{ background: "rgba(12, 37, 64, 0.78)" }}
      >
        <Header user={auth.user} step={step} isReturning={isReturning} />

        <div className="min-h-[260px]">
          {step === 1 && <HandleStep onClaimed={next} initialHandle={handle} isReturning={isReturning} />}
          {step === 2 && <ThemeStep onContinue={next} />}
          {step === 3 && <VaultStep onContinue={next} />}
          {step === 4 && <PrivacyStep onContinue={next} />}
          {step === 5 && <ConnectorCatalogStep onContinue={next} />}
          {step === 6 && <ConnectGoogleStep provider="gmail" onContinue={next} />}
          {step === 7 && <ConnectGoogleStep provider="calendar" onContinue={goWorkspace} finalStep />}
        </div>

        <footer className="flex items-center justify-between gap-2 pt-2 border-t border-white/10">
          <button
            type="button"
            onClick={back}
            disabled={step === 1}
            className="inline-flex items-center gap-1 text-xs text-slate-300/80 hover:text-white disabled:opacity-30"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <span className="text-[11px] text-slate-300/70">
            {isReturning
              ? `Step ${step} of 7 · all steps optional`
              : `Step ${step} of 7 · all steps after handle are skippable`}
          </span>
          {handle ? (
            <button
              type="button"
              onClick={goWorkspace}
              className="inline-flex items-center gap-1 text-xs text-emerald-200 hover:text-white"
              title="Mark onboarding complete and open your workspace. You can revisit any step from /settings."
            >
              Skip and finish <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : step > 1 ? (
            <span className="inline-flex items-center gap-1 text-xs text-amber-300/80">
              Pick a handle to continue
            </span>
          ) : (
            <span aria-hidden />
          )}
        </footer>

        <div className="text-center pt-1">
          <button
            type="button"
            onClick={() => void doLogout()}
            className="text-[11px] text-slate-300/70 hover:text-white underline underline-offset-2"
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}

function Header({
  user,
  step,
  isReturning,
}: {
  user: { name?: string; email?: string; handle?: string } | null;
  step: Step;
  isReturning: boolean;
}) {
  const firstName = user?.name?.split(/\s+/)[0] || "friend";
  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="w-12 h-12 rounded-xl overflow-hidden ring-1 ring-white/15 shadow-md">
          <img src="/icons/icon-512.png" alt="" className="w-full h-full object-cover" />
        </span>
        <div className="flex-1">
          <h1 className="text-xl font-bold tracking-tight lowercase text-white">memso</h1>
          <p className="text-xs text-sky-200/80">
            {isReturning ? `welcome back, ${firstName} · edit your settings` : `welcome, ${firstName}`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-4">
        {STEPS.map((s) => (
          <span
            key={s.idx}
            className={
              "h-1 flex-1 rounded-full transition-colors " +
              (s.idx < step
                ? "bg-emerald-400"
                : s.idx === step
                  ? "bg-sky-400"
                  : "bg-white/15")
            }
          />
        ))}
      </div>
    </div>
  );
}

// =====================================================================
// Step 1: Handle
// =====================================================================

function HandleStep({
  onClaimed,
  initialHandle,
  isReturning,
}: {
  onClaimed: () => void;
  initialHandle: string | null;
  isReturning: boolean;
}) {
  // Seed from server-suggested or current handle so the input never
  // starts blank when the user already has a handle on file. Empty
  // initial state is reserved for genuinely first-run users with no
  // handle yet.
  const [value, setValue] = useState(initialHandle ?? "");
  const [check, setCheck] = useState<{
    available: boolean | null;
    reason: string | null;
    busy: boolean;
  }>({ available: null, reason: null, busy: false });
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // For returning users, default to a read-only view of their existing
  // handle. Clicking "Change handle" expands the editor.
  const [editing, setEditing] = useState(!isReturning || !initialHandle);

  // Keep value in sync if auth resolves after mount (handle prop
  // arrives asynchronously).
  useEffect(() => {
    if (initialHandle && !value) setValue(initialHandle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHandle]);

  useEffect(() => {
    if (!value || value.length < 2) {
      setCheck({ available: null, reason: null, busy: false });
      return;
    }
    setCheck((c) => ({ ...c, busy: true }));
    const t = window.setTimeout(async () => {
      try {
        const res = await fetch(`/api/handle/check?h=${encodeURIComponent(value)}`, {
          credentials: "same-origin",
        });
        const body = (await res.json()) as { handle?: string; available?: boolean; reason?: string | null };
        if (body.handle === value.toLowerCase()) {
          setCheck({ available: !!body.available, reason: body.reason ?? null, busy: false });
        }
      } catch {
        setCheck({ available: null, reason: "network", busy: false });
      }
    }, 250);
    return () => clearTimeout(t);
  }, [value]);

  const claim = async () => {
    if (!value || claiming || check.available === false) return;
    setClaiming(true);
    setError(null);
    try {
      const csrf = getCsrfToken();
      const res = await fetch("/api/handle/claim", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ handle: value }),
      });
      // Defensive: never destructure a field off a response we have
      // not proven the shape of. A non-JSON body (proxy error page),
      // an empty body, or an error envelope must surface a readable
      // message, not crash the screen with "reading 'handle' of
      // undefined" (the 2026-05-18 Claim error).
      let body: {
        ok?: boolean;
        handle?: string;
        user?: { id: string };
        error?: string;
        should_logout?: boolean;
      } | null = null;
      try {
        body = await res.json();
      } catch {
        body = null;
      }
      if (res.status === 401 || body?.should_logout) {
        // Session outlived its user row (delete-account / DB purge).
        // Recover by logging out instead of trapping the user here.
        setError(
          body?.error || "Your session has expired. Signing you out…",
        );
        await doLogout("session_expired");
        return;
      }
      if (!res.ok || !body || !body.ok || !body.user) {
        throw new Error(
          (body && body.error) || `Could not claim handle (HTTP ${res.status})`,
        );
      }
      await refreshAuth();
      onClaimed();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setClaiming(false);
    }
  };

  // Returning user, not actively editing: render the existing handle
  // as a read-only summary with a small Change affordance. Never
  // shows a blank input — the URL preview reflects the stored handle.
  if (!editing && initialHandle) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-bold text-white">Your handle</h2>
          <p className="text-xs text-slate-300/80 mt-1">
            Your workspace lives at <code className="text-sky-200">memso.ai/u/{initialHandle}</code>.
          </p>
        </div>
        <div className="rounded-xl bg-white/5 border border-white/15 px-3 py-3 flex items-center gap-3">
          <span className="text-sm text-slate-300/70 select-none">memso.ai/u/</span>
          <span className="flex-1 text-white font-mono text-sm">{initialHandle}</span>
          <Check className="w-5 h-5 text-emerald-400" />
        </div>
        <button
          type="button"
          onClick={onClaimed}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#f97316] hover:bg-[#fb923c] text-white text-sm font-semibold px-4 py-2.5 transition-colors"
        >
          Continue
        </button>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="w-full text-xs text-sky-200 hover:text-white"
        >
          Change handle
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-bold text-white">
          {isReturning && initialHandle ? "Change your handle" : "Pick your handle"}
        </h2>
        <p className="text-xs text-slate-300/80 mt-1">
          Your workspace lives at <code className="text-sky-200">memso.ai/u/{value || initialHandle || "your-handle"}</code>.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-300/70 select-none">memso.ai/u/</span>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
          maxLength={32}
          autoFocus
          className="flex-1 rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400"
          placeholder="your-handle"
        />
        <span className="w-6 h-6 flex items-center justify-center">
          {check.busy ? (
            <Loader2 className="w-4 h-4 text-slate-300 animate-spin" />
          ) : check.available === true ? (
            <Check className="w-5 h-5 text-emerald-400" />
          ) : check.available === false ? (
            <XIcon className="w-5 h-5 text-rose-400" />
          ) : null}
        </span>
      </div>
      {check.available === false && check.reason && (
        <p className="text-xs text-rose-300">{check.reason}</p>
      )}
      {error && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-2">
          {error}
        </div>
      )}
      <button
        type="button"
        onClick={claim}
        disabled={!value || claiming || check.available === false || check.busy}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#f97316] hover:bg-[#fb923c] text-white text-sm font-semibold px-4 py-2.5 transition-colors disabled:opacity-50"
      >
        {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {initialHandle === value ? "Continue" : claiming ? "Claiming…" : "Claim handle and continue"}
      </button>
      {isReturning && initialHandle && (
        <button
          type="button"
          onClick={() => {
            setValue(initialHandle);
            setEditing(false);
          }}
          className="w-full text-xs text-slate-300/80 hover:text-white"
        >
          Cancel
        </button>
      )}
    </div>
  );
}

// =====================================================================
// Step 2: Theme
// =====================================================================

function ThemeStep({ onContinue }: { onContinue: () => void }) {
  const { theme, setTheme } = useVisualTheme();
  const [picked, setPicked] = useState<VisualTheme>(theme);

  // Filter out legacy themes from the picker.
  const themes = useMemo(
    () => AVAILABLE_THEMES.filter((t) => !t.description.includes("(legacy)")),
    [],
  );

  const apply = (id: VisualTheme) => {
    setPicked(id);
    setTheme(id);
  };

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-base font-bold text-white">Pick a theme</h2>
        <p className="text-xs text-slate-300/80 mt-1">
          You can change this any time from Settings, Appearance. Tap a tile to preview live.
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto pr-1">
        {themes.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => apply(t.id)}
            className={
              "rounded-lg border p-2.5 text-left transition-colors " +
              (picked === t.id
                ? "border-sky-400 bg-white/15 ring-2 ring-sky-300/40"
                : "border-white/15 bg-white/5 hover:bg-white/10")
            }
          >
            <div className="text-xs font-semibold text-white truncate">{t.name}</div>
            <div className="text-[10px] text-slate-300/70 line-clamp-2 mt-0.5">{t.description}</div>
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onContinue}
        className="w-full rounded-xl bg-[#f97316] hover:bg-[#fb923c] text-white text-sm font-semibold px-4 py-2.5 transition-colors"
      >
        Continue
      </button>
    </div>
  );
}

// =====================================================================
// Step 3: Vault path
// =====================================================================

function VaultStep({ onContinue }: { onContinue: () => void }) {
  const [path, setPath] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hadExisting, setHadExisting] = useState(false);

  // Pre-populate from the user's saved vault_path so returning users
  // see their current value, not a blank input. Best-effort; falls
  // through to empty for first-run users.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/user/settings/vault_path", { credentials: "same-origin" });
        if (!res.ok) return;
        const body = (await res.json()) as { value?: unknown };
        if (cancelled) return;
        if (typeof body.value === "string" && body.value) {
          setPath(body.value);
          setHadExisting(true);
        }
      } catch {
        /* silent: first-run users hit this with no value yet */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (skip: boolean) => {
    if (busy) return;
    if (!skip && !path.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (!skip) {
        const csrf = getCsrfToken();
        const res = await fetch("/api/user/settings", {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            ...(csrf ? { "X-CSRF-Token": csrf } : {}),
          },
          body: JSON.stringify({ key: "vault_path", value: path.trim() }),
        });
        if (!res.ok) throw new Error(`save failed: ${res.status}`);
      }
      onContinue();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-base font-bold text-white">
          {hadExisting ? "Your vault" : "Vault path"}
        </h2>
        <p className="text-xs text-slate-300/80 mt-1">
          Memso writes daily briefs and curated notes to a folder on your computer
          (typically your Obsidian vault). Leave blank to use the default at
          <code className="text-sky-200"> ~/memso-vault</code>; you can change it later.
        </p>
      </div>
      <input
        type="text"
        value={path}
        onChange={(e) => setPath(e.target.value)}
        placeholder="~/Documents/Obsidian/MemsoVault"
        className="w-full rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400 font-mono text-sm"
      />
      {error && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-2">
          {error}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => save(true)}
          disabled={busy}
          className="flex-1 rounded-xl bg-white/10 border border-white/15 text-white text-sm font-semibold px-4 py-2.5 hover:bg-white/20 disabled:opacity-50"
        >
          {hadExisting ? "Keep current" : "Skip for now"}
        </button>
        <button
          type="button"
          onClick={() => save(false)}
          disabled={busy || !path.trim()}
          className="flex-1 rounded-xl bg-[#f97316] hover:bg-[#fb923c] text-white text-sm font-semibold px-4 py-2.5 disabled:opacity-50"
        >
          {busy ? "Saving…" : hadExisting ? "Update and continue" : "Save and continue"}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Step 4: Privacy scopes
// =====================================================================

// Three mutually-exclusive primaries. Personal carries sub-categories;
// Work is tagged with the current employer; Private is auto-detected.
const PRIMARY_ROWS: {
  scope: string;
  tone: string;
  sample: string;
  subs?: string[];
}[] = [
  {
    scope: "personal",
    tone: "emerald",
    sample:
      "Everything from your personal accounts. Sub-categories let you filter by Family, Social, Medical, Financial, Business, or General personal.",
    subs: ["General", "Family", "Social", "Medical", "Financial", "Business"],
  },
  {
    scope: "work",
    tone: "sky",
    sample:
      "Your day-job employer accounts. Each captured packet is tagged with the current employer (OpenText today; you can add another when you change jobs).",
  },
  {
    scope: "private",
    tone: "rose",
    sample:
      "NSFW, explicit, or compromising content. Memso auto-detects this from content. You can always re-scope a packet from its menu.",
  },
];

function PrivacyStep({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-base font-bold text-white">Privacy scopes</h2>
        <p className="text-xs text-slate-300/80 mt-1">
          Every capture lands in one of three primaries.{" "}
          <strong className="text-white">private</strong> stays local-only
          and never reaches a cloud model. The pipeline auto-classifies;
          you can always re-scope from any capture's menu.
        </p>
      </div>
      <ul className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
        {PRIMARY_ROWS.map((s) => (
          <li
            key={s.scope}
            className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 space-y-2"
          >
            <div className="flex items-start gap-2">
              <span
                className={
                  "text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full mt-0.5 flex-shrink-0 " +
                  tintClasses(s.tone)
                }
              >
                {s.scope}
              </span>
              <span className="text-xs text-slate-300/85">{s.sample}</span>
            </div>
            {s.subs && (
              <div className="flex flex-wrap gap-1 pl-1">
                {s.subs.map((sub) => (
                  <span
                    key={sub}
                    className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-white/10 text-slate-200 border border-white/15"
                  >
                    {sub}
                  </span>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onContinue}
        className="w-full rounded-xl bg-[#f97316] hover:bg-[#fb923c] text-white text-sm font-semibold px-4 py-2.5"
      >
        Got it, continue
      </button>
    </div>
  );
}

function tintClasses(tone: string): string {
  switch (tone) {
    case "rose": return "bg-rose-500/20 text-rose-200 border border-rose-400/30";
    case "sky": return "bg-sky-500/20 text-sky-200 border border-sky-400/30";
    case "violet": return "bg-violet-500/20 text-violet-200 border border-violet-400/30";
    case "emerald": return "bg-emerald-500/20 text-emerald-200 border border-emerald-400/30";
    case "amber": return "bg-amber-500/20 text-amber-200 border border-amber-400/30";
    case "pink": return "bg-pink-500/20 text-pink-200 border border-pink-400/30";
    default: return "bg-white/10 text-white border border-white/20";
  }
}

// =====================================================================
// Step 5: Connector catalog chooser
// =====================================================================

function ConnectorCatalogStep({ onContinue }: { onContinue: () => void }) {
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const proceed = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (selected.length > 0) {
        const csrf = getCsrfToken();
        await fetch("/api/user/settings", {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            ...(csrf ? { "X-CSRF-Token": csrf } : {}),
          },
          body: JSON.stringify({ key: "onboarding_connectors", value: selected.join(",") }),
        }).catch(() => undefined);
        // Record interest for everything the user picked; the backend
        // ignores already-built rows gracefully (they just upsert a
        // request row that is harmless). Best-effort, in parallel.
        await Promise.allSettled(selected.map((slug) => api.requestConnector(slug)));
      }
    } finally {
      setBusy(false);
      onContinue();
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-base font-bold text-white">Choose what to connect</h2>
        <p className="text-xs text-slate-300/80 mt-1">
          This is everything Memso can pull from, organized by category. Tap any
          number to line them up. Built connectors you can wire right after this;
          the rest we will prioritize based on what you pick. You can skip and do
          this later from Settings.
        </p>
      </div>
      <div className="max-h-[320px] overflow-y-auto pr-1 rounded-xl">
        <ConnectorCatalog
          mode="onboarding"
          selected={selected}
          onSelectedChange={setSelected}
        />
      </div>
      <button
        type="button"
        onClick={proceed}
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#f97316] hover:bg-[#fb923c] text-white text-sm font-semibold px-4 py-2.5 transition-colors disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {selected.length > 0 ? `Continue with ${selected.length} selected` : "Skip for now"}
      </button>
    </div>
  );
}

// =====================================================================
// Steps 6+7: Connect Gmail / Calendar
// =====================================================================

function ConnectGoogleStep({
  provider,
  onContinue,
  finalStep,
}: {
  provider: "gmail" | "calendar";
  onContinue: () => void;
  finalStep?: boolean;
}) {
  const [connections, setConnections] = useState<{ provider: string; account_email: string }[] | null>(null);

  useEffect(() => {
    fetch("/api/connectors/google/connections", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((b: { connections?: { provider: string; account_email: string }[] }) => setConnections(b.connections || []))
      .catch(() => setConnections([]));
  }, []);

  const connected = (connections || []).filter((c) => c.provider === provider);
  const Icon = provider === "gmail" ? Mail : Calendar;
  const blurb = provider === "gmail"
    ? "Pull recent threads (default 7 days back). Skip spam, promotions, social, forums; keep personal mail + transactional updates (orders, receipts, account confirmations) automatically."
    : "Read your calendar past + future. Skip cancelled events + events you declined. Each external attendee becomes a person in your graph.";
  const startUrl = `/api/connectors/google/start?provider=${encodeURIComponent(provider)}&scope=${provider === "calendar" ? "personal" : "work"}&returnTo=/onboarding`;

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2.5">
        <span className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center text-sky-200 flex-shrink-0">
          <Icon className="w-4 h-4" />
        </span>
        <div>
          <h2 className="text-base font-bold text-white capitalize">Connect {provider}</h2>
          <p className="text-xs text-slate-300/80 mt-1">{blurb}</p>
        </div>
      </div>

      {connected.length > 0 ? (
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/30 px-3 py-2 text-xs text-emerald-200">
          <div className="font-semibold">Connected</div>
          {connected.map((c) => (
            <div key={c.account_email} className="font-mono text-emerald-100/80">
              {c.account_email}
            </div>
          ))}
        </div>
      ) : (
        <a
          href={startUrl}
          className="block w-full text-center rounded-xl bg-[#f97316] hover:bg-[#fb923c] text-white text-sm font-semibold px-4 py-2.5 transition-colors"
        >
          Connect with Google
        </a>
      )}

      <button
        type="button"
        onClick={onContinue}
        className="w-full rounded-xl bg-white/10 border border-white/15 text-white text-sm font-semibold px-4 py-2.5 hover:bg-white/20"
      >
        {connected.length > 0
          ? finalStep
            ? "Open my workspace"
            : "Continue"
          : finalStep
            ? "Skip and open my workspace"
            : "Skip for now"}
      </button>
    </div>
  );
}
